import { useState } from "react"
import FieldInfoIcon from "../../components/field-info"
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../../components/ui/form"
import { Input } from "../../components/ui/input"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select"
import { cn, parseAttributes } from "../../lib/utils"
import {
	DropdownConfig,
	type Attribute,
	type FieldProps,
} from "../../types/form-types"
import { Description } from "./components/description"
import { useFormStore } from "@/stores/use-form-store"
import { useLogicStore } from "@/stores/use-conditional-logic-store"
import { ErrorMessage } from "./components/error-message"

interface SelectFieldProps extends FieldProps {
	optionsPreSet?: { label: string; value: string }[]
	isDisabled?: boolean
}

export const SelectField = ({
	form,
	field,
	commonAttributes,
	optionsPreSet,
	isDisabled = false,
}: SelectFieldProps) => {
	const [isOtherSelected, setIsOtherSelected] = useState(false)
	const { label, infoIconText, infoIconType, description, infoIconRequired } =
		commonAttributes

	const { options, allowOtherOption, textInputRequired, sortingOfOptions } =
		getSelectInputAttributes(field.attributes)

	const { shouldDisable, verifiedFields } = useFormStore()

	const isVerified = verifiedFields.get(field.field_id) || false

	let sortedOptions: { label: string; value: string }[] =
		optionsPreSet ?? (options || [])
	if (sortingOfOptions) {
		if (sortingOfOptions === "ALPHABETICAL") {
			sortedOptions = [...sortedOptions].sort((a, b) =>
				a.label.localeCompare(b.label),
			)
		} else if (sortingOfOptions === "ALPHABETICAL_DESCENDING") {
			sortedOptions = [...sortedOptions].sort((a, b) =>
				b.label.localeCompare(a.label),
			)
		}
	}

	const { hiddenFields, errors } = useLogicStore()

	if (hiddenFields[field.field_id]) return null

	return (
		<FormField
			control={form.control}
			name={field.field_id}
			render={({ field: formField }) => (
				<FormItem>
					<FormLabel>
						<div className="flex flex-col gap-1">
							<div className="flex items-center gap-1">
								<p
									className={cn(
										field.is_required &&
											"after:ml-1 after:text-red-500 after:content-['*']",
									)}
								>
									{label}
								</p>
								{infoIconRequired && (
									<FieldInfoIcon
										infoText={infoIconText}
										iconType={infoIconType}
									/>
								)}
							</div>
							{description && (
								<Description description={description} />
							)}
						</div>
					</FormLabel>
					<Select
						disabled={isVerified || isDisabled || shouldDisable}
						onValueChange={val => {
							if (textInputRequired && val === "other") {
								setIsOtherSelected(true)
								formField.onChange("") // reset so input can take over
							} else {
								setIsOtherSelected(false)
								formField.onChange(val)
							}
						}}
						value={isOtherSelected ? "other" : formField.value}
					>
						<FormControl>
							<SelectTrigger>
								<SelectValue placeholder="Select a option" />
							</SelectTrigger>
						</FormControl>
						<SelectContent>
							{sortedOptions.length > 0 ? (
								sortedOptions.map(option => (
									<SelectItem
										value={option.value}
										key={option.value}
									>
										{option.label}
									</SelectItem>
								))
							) : (
								<SelectItem
									value="__disabled__"
									disabled
								>
									No options are available
								</SelectItem>
							)}
							{allowOtherOption && (
								<SelectItem value="other">Other</SelectItem>
							)}
						</SelectContent>
					</Select>

					{/* Show input if "Other" is selected */}
					{allowOtherOption &&
						textInputRequired &&
						isOtherSelected && (
							<div className="mt-2">
								<Input
									value={formField.value}
									onChange={e =>
										formField.onChange(e.target.value)
									}
									required={field.is_required}
								/>
							</div>
						)}
					<FormMessage />
					<ErrorMessage error={errors[field.field_id]} />
				</FormItem>
			)}
		/>
	)
}

function getSelectInputAttributes(attributes: Attribute[]) {
	const props: {
		options: { label: string; value: string }[]
		allowOtherOption: boolean
		textInputRequired: boolean
		defaultSelection: string[]
		sortingOfOptions?: string
	} = {
		options: [],
		allowOtherOption: false,
		textInputRequired: false,
		defaultSelection: [],
	}
	const parsed = parseAttributes(attributes)

	Object.entries(parsed).forEach(([key, value]) => {
		switch (key) {
			case DropdownConfig.OptionList:
				props.options = value as { label: string; value: string }[]
				break
			case DropdownConfig.AllowOtherOption:
				props.allowOtherOption = value as boolean
				break
			case DropdownConfig.TextInputRequired:
				props.textInputRequired = value as boolean
				break
			case DropdownConfig.DefaultSelection:
				props.defaultSelection = value as string[]
				break
			case DropdownConfig.SortingOfOptions:
				props.sortingOfOptions = value as string
				break
		}
	})

	return props
}
